import { registerComponent } from "helpers";
import WYATTElement from "wyatt";

const productFormTemplate = (id) => `
  <product-form>
    <form>
      <input type="hidden" name="id" value="${id}">
      <input type="hidden" name="quantity" value="1">
    </form>
  </product-form>
`;

const productAvailabilityTemplate = (id, storeNumber) => `
  <product-availability>
    <script type="application/json" data-config>
      {
        "quantity": 1,
        "storeNumber": ${storeNumber},
        "variant": ${id}
      }
    </script>
  </product-availability>
`;

registerComponent(
  "flipp-flyer",
  class FlippFlyer extends WYATTElement {
    constructor() {
      super();

      this.container = this.querySelector("[data-flipp-container]");
      this.onPreferredStoreChange = this.handlePreferredStoreChange.bind(this);
    }

    connectedCallback() {
      this.subscribe(
        WYATT.PubSub.events.preferredStoreChange,
        this.onPreferredStoreChange,
      );

      // preferredStoreChange only fires once a store is set; load locale
      // defaults so the flyer still renders for shoppers without a selection.
      if (!WYATT.PreferredStores?.store?.store_number) {
        this.handlePreferredStoreChange(null);
      }
    }

    async handlePreferredStoreChange(store) {
      this.storeNumber = store?.store_number ?? this.config.defaultStoreCode;

      if (!this.storeNumber) {
        return;
      }

      try {
        await this.loadScriptOnce();
      } catch (error) {
        window.console.error(error);
        return;
      }

      if (!window.Flipp?.Storefront) {
        window.console.error(
          "Flipp.Storefront is not available after script load",
        );
        return;
      }

      this.container.innerHTML = "";
      this.flippStorefront = new window.Flipp.Storefront({
        accessToken: this.config.accessToken,
        merchantId: this.config.merchantId,
        merchantNameIdentifier: this.config.merchantNameIdentifier,
        locale: this.config.locale,
        storeCode: `${this.storeNumber}`,
        cartDelegate: {
          addToCart: this.addToMerchantCart.bind(this),
          getCartOptions: this.getMerchantCartOptions.bind(this),
        },
      });

      this.flippStorefront.renderStorefront(this.container);
    }

    async loadScriptOnce() {
      if (this.script) {
        return Promise.resolve(true);
      }

      return new Promise((resolve, reject) => {
        this.script = document.createElement("script");
        this.script.src = this.config.iframeScriptUrl;
        this.script.onload = resolve;
        this.script.onerror = reject;
        this.appendChild(this.script);
      });
    }

    async addToMerchantCart(item) {
      const variantId = await this.getVariantId(item.sku);
      const div = document.createElement("div");
      div.innerHTML = productFormTemplate(variantId);

      const ProductForm = div.querySelector("product-form");

      ProductForm.addToCart();
    }

    async getVariantId(sku) {
      try {
        const response = await fetch(
          `${WYATT.config.flyer.endpoints.variantLookup}/${sku}`,
        );

        if (!response.ok) {
          throw new Error(
            `Failed to get variant id for SKU ${sku}: ${response.statusText}`,
          );
        }

        const { variantId } = await response.json();

        return variantId ?? undefined;
      } catch (error) {
        window.console.error(`Failed to get variant id for SKU ${sku}:`, error);
        return undefined;
      }
    }

    async getMerchantCartOptions(items) {
      const options = await Promise.all(
        items.map((item) => this.getMerchantCartOptionForItem(item.sku)),
      );

      return options;
    }

    async getMerchantCartOptionForItem(id) {
      const cartabilityType = await this.getCartabilityType(id);

      return [
        {
          cartabilityType,
          id,
          label: {
            "en-CA": "Add to Cart",
            "fr-CA": "Ajouter au panier",
          },
        },
      ];
    }

    async getCartabilityType(sku) {
      const variantId = await this.getVariantId(sku);

      const div = document.createElement("div");
      div.innerHTML = productAvailabilityTemplate(variantId, this.storeNumber);

      const { StoreAvailability } = div.querySelector("product-availability");

      await StoreAvailability.getAvailability();

      return StoreAvailability.isAvailable ? 1 : 0;
    }
  },
);
